import styled, { css } from "styled-components";
import { Link, NavLink } from "react-router-dom";
import { ChevronDown } from "lucide-react";

export const Nav = styled.nav`
  position: fixed;
  top: ${({ $promoHeight }) =>
    $promoHeight ? `${$promoHeight + 12}px` : "12px"};
  left: 0;
  right: 0;
  z-index: 1000;
  transition: top 0.3s ease;
`;

export const Inner = styled.div`
  max-width: ${({ $scrolled }) => ($scrolled ? "820px" : "100%")};
  margin: 0 auto;
  padding: ${({ $scrolled }) => ($scrolled ? "10px 24px" : "14px 32px")};
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: ${({ $scrolled, theme }) =>
    $scrolled
      ? theme.light
        ? "rgba(255, 255, 255, 0.85)"
        : "rgba(10, 10, 10, 0.85)"
      : "transparent"};
  border: 1px solid
    ${({ $scrolled, theme }) =>
      $scrolled ? theme.cardBorder : "rgba(255, 255, 255, 0)"};
  backdrop-filter: ${({ $scrolled }) =>
    $scrolled ? "blur(20px)" : "blur(0px)"};
  -webkit-backdrop-filter: ${({ $scrolled }) =>
    $scrolled ? "blur(20px)" : "blur(0px)"};
  border-radius: 0;
  transition:
    max-width 0.4s cubic-bezier(0.16, 1, 0.3, 1),
    padding 0.4s cubic-bezier(0.16, 1, 0.3, 1),
    background 0.4s cubic-bezier(0.16, 1, 0.3, 1),
    border-color 0.4s cubic-bezier(0.16, 1, 0.3, 1),
    backdrop-filter 0.4s cubic-bezier(0.16, 1, 0.3, 1),
    -webkit-backdrop-filter 0.4s cubic-bezier(0.16, 1, 0.3, 1);

  @media (max-width: 768px) {
    max-width: ${({ $scrolled }) => ($scrolled ? "95%" : "100%")};
  }
`;

export const LogoLink = styled(Link)`
  display: flex;
  align-items: center;
  gap: 10px;
  text-decoration: none;
  color: #fff;
  svg {
    height: 14px;
    width: auto;
  }
`;

export const NavLinks = styled.div`
  display: flex;
  align-items: center;
  gap: 28px;
  @media (max-width: 768px) {
    display: none;
  }
`;

const navLinkStyles = css`
  text-decoration: none;
  font-size: 0.85rem;
  font-weight: 500;
  letter-spacing: 1px;
  text-transform: uppercase;
  transition: color 0.3s ease;
  color: ${({ theme }) => theme.textSecondary};
`;

export const NavAnchor = styled.a`
  ${navLinkStyles}
  &:hover {
    color: ${({ theme }) => theme.accent};
  }
`;

export const NavRouterLink = styled(NavLink)`
  ${navLinkStyles}
  &:hover {
    color: ${({ theme }) => theme.accent};
  }
  &.active {
    color: ${({ theme }) => theme.accent};
  }
`;

export const MiaLink = styled(NavLink)`
  ${navLinkStyles}
  color: ${({ theme }) => theme.accent};
  font-style: italic;
  font-weight: 700;
  &:hover {
    color: #fff;
  }
  &.active {
    color: #fff;
  }
`;

export const CartLink = styled(NavLink)`
  position: relative;
  color: ${({ theme }) => theme.textSecondary};
  transition: color 0.3s ease;
  display: flex;
  align-items: center;
  &:hover {
    color: ${({ theme }) => theme.accent};
  }
  &.active {
    color: ${({ theme }) => theme.accent};
  }
`;

export const CartBadge = styled.span`
  position: absolute;
  top: -6px;
  right: -8px;
  background: ${({ theme }) => theme.accent};
  color: #000;
  font-size: 0.58rem;
  font-weight: 800;
  min-width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 4px;
  font-family: ${({ theme }) => theme.mono};
`;

export const SettingsWrap = styled.div`
  display: flex;
  align-items: center;

  @media (max-width: 768px) {
    display: none;
  }
`;

/* ── Dropdown ── */
export const DropdownWrap = styled.div`
  position: relative;
`;

export const DropdownTrigger = styled.button`
  ${navLinkStyles}
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 0;
  font-family: ${({ theme }) => theme.font};
  ${({ $active, theme }) => $active && `color: ${theme.accent};`}
  &:hover {
    color: ${({ theme }) => theme.accent};
  }
`;

export const DropdownMenu = styled.div`
  position: absolute;
  top: calc(100% + 12px);
  left: 50%;
  transform: translateX(-50%);
  background: ${({ theme }) =>
    theme.light ? "rgba(255, 255, 255, 0.95)" : "rgba(10, 10, 10, 0.95)"};
  border: 1px solid ${({ theme }) => theme.cardBorder};
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  min-width: 160px;
  padding: 8px 0;
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  visibility: ${({ $open }) => ($open ? "visible" : "hidden")};
  transform: translateX(-50%)
    translateY(${({ $open }) => ($open ? "0" : "-8px")});
  transition:
    opacity 0.2s ease,
    transform 0.2s ease,
    visibility 0.2s;
`;

export const DropdownItem = styled(NavLink)`
  display: block;
  padding: 10px 20px;
  color: ${({ theme }) => (theme.light ? "#666666" : "#888888")};
  text-decoration: none;
  font-size: 0.82rem;
  font-weight: 500;
  letter-spacing: 1px;
  text-transform: uppercase;
  transition:
    color 0.2s,
    background 0.2s;
  &:hover {
    color: ${({ theme }) => theme.accent};
    background: ${({ theme }) =>
      theme.light ? "rgba(0, 0, 0, 0.03)" : "rgba(255, 255, 255, 0.03)"};
  }
  &.active {
    color: ${({ theme }) => theme.accent};
  }
`;

export const ChevronIcon = styled(ChevronDown)`
  transition: transform 0.2s ease;
  transform: rotate(${({ $open }) => ($open ? "180deg" : "0")});
`;

/* ── Mobile right container ── */
export const MobileRight = styled.div`
  display: none;
  align-items: center;
  gap: 16px;
  z-index: 1100;

  @media (max-width: 768px) {
    display: flex;
  }
`;

export const MobileCartLink = styled(NavLink)`
  position: relative;
  color: ${({ $scrolled, theme }) =>
    $scrolled ? theme.textSecondary : "#ffffff"};
  transition: color 0.3s ease;
  display: flex;
  align-items: center;
  &:hover {
    color: ${({ theme }) => theme.accent};
  }
  &.active {
    color: ${({ theme }) => theme.accent};
  }
`;

export const MobileCartBadge = styled.span`
  position: absolute;
  top: -6px;
  right: -8px;
  background: ${({ theme }) => theme.accent};
  color: #000;
  font-size: 0.58rem;
  font-weight: 800;
  min-width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 4px;
  font-family: ${({ theme }) => theme.mono};
`;

/* ── Hamburger ── */
export const HamburgerButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  color: ${({ $scrolled, theme }) =>
    $scrolled ? (theme.light ? "#666666" : "#888888") : "#ffffff"};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.3s ease;
`;

/* ── Drawer close - fixed at same visual position as hamburger ── */
export const DrawerClose = styled.button`
  display: none;

  @media (max-width: 768px) {
    display: flex;
    position: fixed;
    top: 28px;
    right: 33px;
    z-index: 1100;
    background: none;
    border: none;
    cursor: pointer;
    padding: 4px;
    color: ${({ theme }) => (theme.light ? "#666666" : "#888888")};
    align-items: center;
    justify-content: center;
  }
`;

/* ── Mobile overlay ── */
export const MobileOverlay = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: block;
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.6);
    backdrop-filter: blur(4px);
    -webkit-backdrop-filter: blur(4px);
    z-index: 999;
    opacity: ${({ $open }) => ($open ? 1 : 0)};
    visibility: ${({ $open }) => ($open ? "visible" : "hidden")};
    transition:
      opacity 0.35s ease,
      visibility 0.35s ease;
  }
`;

export const MobileMenu = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    width: min(320px, 85vw);
    background: ${({ theme }) =>
      theme.light ? "rgba(255, 255, 255, 0.97)" : "rgba(13, 13, 13, 0.97)"};
    border-left: 1px solid ${({ theme }) => theme.cardBorder};
    backdrop-filter: blur(8px);
    -webkit-backdrop-filter: blur(8px);
    z-index: 1050;
    padding: 100px 32px 48px;
    transform: translateX(${({ $open }) => ($open ? "0" : "100%")});
    transition: transform 0.4s cubic-bezier(0.16, 1, 0.3, 1);
    overflow-y: auto;
  }
`;

export const MobileNavLinks = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
`;

const mobileNavLinkStyles = css`
  text-decoration: none;
  font-size: 1rem;
  font-weight: 500;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  padding: 14px 0;
  border-bottom: 1px solid ${({ theme }) => theme.cardBorder};
  transition:
    color 0.2s ease,
    padding-left 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: space-between;

  &:hover {
    color: ${({ theme }) => theme.accent};
    padding-left: 6px;
  }
`;

export const MobileNavAnchor = styled.a`
  ${mobileNavLinkStyles}
  color: ${({ theme }) => (theme.light ? "#666666" : "#888888")};
`;

export const MobileNavRouterLink = styled(NavLink)`
  ${mobileNavLinkStyles}
  color: ${({ theme }) => (theme.light ? "#666666" : "#888888")};

  &.active {
    color: ${({ theme }) => theme.accent};
  }
`;

export const MobileMiaLink = styled(NavLink)`
  ${mobileNavLinkStyles}
  color: ${({ theme }) => theme.accent};
  font-style: italic;
  font-weight: 700;

  &:hover {
    color: #ffffff;
    padding-left: 6px;
  }

  &.active {
    color: #ffffff;
  }
`;
